import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useCreateTenderContactPersonMutation,
  useDeleteTenderContactPersonMutation,
  useTenderContactPeopleQuery,
} from 'services/tender/tenderContactPersons'
import { v4 as uuidv4 } from 'uuid'
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { ITenderContactPerson } from 'types/tender/tenderContactPersons'

type Props = {
  tenderId: string
  isDisabled?: boolean
}

export const TenderContactPeople = ({ tenderId, isDisabled = false }: Props) => {
  const { t } = useTranslation()

  const defaultNewItem: Partial<ITenderContactPerson> = {
    id: uuidv4(),
    position: '',
    email: '',
    phoneNumber: '',
    fullName: '',
    tenderId,
  }

  const [newItem, setNewItem] =
    useState<Partial<ITenderContactPerson>>(defaultNewItem)

  const { data } = useTenderContactPeopleQuery({ tenderId }, { skip: !tenderId })
  const [deleteContact, { isLoading: isDeleteLoading }] =
    useDeleteTenderContactPersonMutation()
  const [addContact, { isLoading: isAddLoading, isSuccess: isAddSuccess }] =
    useCreateTenderContactPersonMutation()

  useEffect(() => {
    if (isAddSuccess) {
      setNewItem(defaultNewItem)
    }
  }, [isAddSuccess])

  const handleDelete = useCallback(
    (id: string) => {
      deleteContact({ id })
    },
    [deleteContact]
  )

  const handleItemDetailsChange = useCallback(
    (
      key: keyof ITenderContactPerson,
      value: ITenderContactPerson[keyof ITenderContactPerson]
    ) => {
      setNewItem((old) => ({
        ...old,
        [key]: value,
      }))
    },
    []
  )

  const handleAddition = useCallback(() => {
    addContact(newItem)
  }, [addContact, newItem])

  const isNewItemValid = useMemo(
    () =>
      !!newItem.phoneNumber &&
      !!newItem.email &&
      !!newItem.fullName &&
      !!newItem.position,
    [newItem]
  )

  return (
    <Box mt={2}>
      <Box mb={1.5}>
        <Typography fontWeight="700">{t('tenderContactPeopleTitle')}:</Typography>
      </Box>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 650, borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('fullName')}</TableCell>
              <TableCell>{t('position')}</TableCell>
              <TableCell>{t('email-address')}</TableCell>
              <TableCell>{t('phone-number')}</TableCell>
              {!isDisabled && <TableCell width={1} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(({ fullName, position, email, phoneNumber, id }) => (
              <TableRow key={id}>
                <TableCell>{fullName}</TableCell>
                <TableCell>{position}</TableCell>
                <TableCell>{email}</TableCell>
                <TableCell>{phoneNumber}</TableCell>
                {!isDisabled && (
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <IconButton
                        size="small"
                        disabled={isDeleteLoading}
                        onClick={() => handleDelete(id)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!isDisabled && (
              <TableRow>
                <TableCell sx={{ pt: 3 }}>
                  <TextField
                    size="small"
                    label={t('fullName')}
                    value={newItem.fullName}
                    onChange={(e) =>
                      handleItemDetailsChange('fullName', e.target.value)
                    }
                  />
                </TableCell>
                <TableCell sx={{ pt: 3 }}>
                  <TextField
                    size="small"
                    label={t('position')}
                    value={newItem.position}
                    onChange={(e) =>
                      handleItemDetailsChange('position', e.target.value)
                    }
                  />
                </TableCell>
                <TableCell sx={{ pt: 3 }}>
                  <TextField
                    type={'email'}
                    size="small"
                    label={t('email-address')}
                    value={newItem.email}
                    onChange={(e) =>
                      handleItemDetailsChange('email', e.target.value)
                    }
                  />
                </TableCell>
                <TableCell sx={{ pt: 3 }}>
                  <TextField
                    size="small"
                    label={t('phone-number')}
                    value={newItem.phoneNumber}
                    onChange={(e) =>
                      handleItemDetailsChange('phoneNumber', e.target.value)
                    }
                  />
                </TableCell>
                <TableCell sx={{ pt: 3 }}>
                  <Box display="flex" alignItems="center">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={isAddLoading || !isNewItemValid}
                      onClick={handleAddition}
                    >
                      <Add />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
